import { forwardRef, useState, useEffect, Fragment, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import deviceHelper from '@utils/deviceHelper';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { toggleEnrolledCourse } from '@actions/courses';
import Carousel from '@comp/Carousel/Carousel';
import FreePlanUnlock from '@comp/FreePlanUnlock/FreePlanUnlock';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import H2 from '@base/H2/H2';
import H4 from '@base/H4/H4';
import { Spacings, TextColors, Fonts, breakpoints } from '@styles/index';
import dateImage from '@images/prodigy/date.svg';
import { unlockFreePlanFor } from '@constants/index';
import analyticsAction, { segment } from '@actions/analytics';
import { GTM_TAP_ENROLL } from '@constants/segment';
import BlocksWrapper from './BlocksWrapper';

const StyledCarousel = styled(Carousel)`
  margin: ${Spacings.SPACING_0B};
`;

const LeftColumn = styled.div`
  flex: 1;
  position: sticky;
  height: fit-content;
  top: 30px;
  img {
    height: 48px;
    width: 48px;
  }
  div:not(:last-child) {
    padding-bottom: ${Spacings.SPACING_4B};
  }
`;

const Heading = styled(H2)`
  margin: ${Spacings.SPACING_4B} ${Spacings.SPACING_0B} 62px
    ${Spacings.SPACING_0B};
  font-size: ${Fonts.H2} !important;
`;

const RightColumn = styled.div`
  flex: 2;
  margin-bottom: -${Spacings.SPACING_8B};
  margin-left: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
    flex: 1;
    margin-left: ${Spacings.SPACING_0B};
  }
`;

const TestCardsContainer = styled.div`
  margin-left: ${Spacings.SPACING_24B};
  max-width: 770px;
  height: ${({ customHeight }) =>
    customHeight ? `${customHeight}px` : 'auto'};
  overflow-y: hidden;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    height: auto;
    width: 100%;
    width: ${({ customWidth }) => (customWidth ? `${customWidth}px` : 'auto')};
    margin: ${Spacings.SPACING_0B};
    overflow-y: visible;
    overflow-x: hidden;
  }
`;

const InnerContainer = styled.div`
  padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  height: ${({ customHeight }) =>
    customHeight ? `${customHeight}px` : 'auto'};
  transform: ${({ pos, customHeight }) =>
    (pos && `translate3d(0, ${-customHeight * pos}px, 0)`) || 'initial'};
  transition: transform 400ms ease-in;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    height: auto;
    width: ${({ customWidth }) =>
      customWidth ? `${3 * customWidth}px` : 'auto'};
    transform: unset;
    flex-direction: row;
    padding: ${Spacings.SPACING_0B};
    display: flex;
  }
`;

const GoalTabs = styled.div`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: flex;
    overflow: auto;
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    scrollbar-width: none; /* Firefox */
    /* Chrome and IOS Safari */
    ::-webkit-scrollbar {
      width: 0px;
      background: transparent;
    }
    div:not(:last-child) {
      margin-right: ${Spacings.SPACING_2B};
    }
  }
`;

const GoalBlockContainer = styled.div`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    flex: 1 0 auto;
  }
`;

const GoalBlock = styled.div`
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_16B} ${Spacings.SPACING_4B}
    ${Spacings.SPACING_6B};
  background: ${({ isEnabled }) => (isEnabled ? TextColors.WHITE : '#F7F9FA')};
  box-shadow: ${({ isEnabled, theme }) =>
    isEnabled ? boxShadowGenerator({ theme }) : 'none'};
  border-radius: ${Spacings.SPACING_2B};
  height: 64px;
  width: 270px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: fit-content;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
    height: 50px;
    display: flex;
    align-items: center;
    h4 {
      font-size: ${Fonts.H6};
    }
  }
  cursor: pointer;
  transition: all 200ms ease-in;
`;

const TestsContainer = styled.div`
  margin-bottom: ${Spacings.SPACING_8B};
  height: ${({ customHeight }) =>
    customHeight ? `${customHeight - 32}px` : 'auto'};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    height: auto;
    width: ${({ customWidth }) => (customWidth ? `${customWidth}px` : 'auto')};
    flex: 0 0 auto;
    display: ${({ index, pos, customWidth }) =>
      !customWidth && index !== pos ? 'none' : 'flex'};
    transform: ${({ pos, customWidth }) =>
      (pos && `translate3d(${-customWidth * pos}px, 0, 0)`) || 'initial'};
    overflow-y: visible;
    flex-direction: column;
    margin-bottom: ${Spacings.SPACING_0B};
  }
  display: ${({ index, pos, customHeight }) =>
    !customHeight && index !== pos ? 'none' : 'flex'};
  visibility: ${({ index, pos }) => (index !== pos ? 'hidden' : 'visible')};
  opacity: ${({ index, pos }) => (index !== pos ? 0 : 1)};
  transition: all 400ms ease-in;
`;

const GoalWiseTestSeries = forwardRef(
  ({ items, id, title, goalData, Card, data }, ref) => {
    const [status, setStatus] = useState({});
    const [freePlanUnlockDrawerState, setFreePlanUnlockDrawerState] = useState(
      false
    );
    const closeFreePlanUnlockDrawer = () => setFreePlanUnlockDrawerState(false);
    const [objectUid, setObjectUid] = useState(null);

    const dispatch = useDispatchToStore();
    const { query } = useRouter();
    const { testSeriesKey } = query;
    const enrollStatuses = useSelector(
      (state) => state.courses.checkEnrollResults
    );
    const me = useSelector((state) => state.auth.me);
    const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn);
    const isPlatformUnlocked = me.platformUnlockStatus?.is_platform_unlocked;

    useEffect(() => {
      setStatus(enrollStatuses);
    }, [enrollStatuses]);

    const onClickEnroll = (enroll, uid, classOrTest) => {
      if (enroll) {
        const newStatus = { ...status };
        if (deviceHelper.isMobileDevice() && !me.username) {
          window.location.href = `/login?redirect=/event/testseries/${testSeriesKey}`;
          return;
        }
        if (isLoggedIn && !isPlatformUnlocked) {
          setObjectUid(uid);
          setFreePlanUnlockDrawerState(true);
        } else {
          dispatch(toggleEnrolledCourse(uid, enroll, true)).then((response) => {
            if (!response?.error && me.username) {
              newStatus[uid] = true;
              setStatus(newStatus);
              const isClass = !classOrTest.quizUid;
              const props = isClass
                ? {
                    'Class Name': classOrTest.title,
                    'Test Name': classOrTest.title,
                    'Class Uid': classOrTest.uid,
                    'Educator Name': classOrTest.authorName
                  }
                : {
                    'Test Name': classOrTest.name,
                    'Test Uid': classOrTest.quizUid
                  };
              const analyticsProps = {
                ...props,
                'Start Time': classOrTest.startsAt,
                'End Time': classOrTest.endsAt,
                Subject: classOrTest.topics,
                'Test Series Uid': uid,
                'GTM Initiative': data.header?.title || '',
                'Goal Id': '',
                'Goal Name': ''
              };
              dispatch(
                analyticsAction(
                  'gtm-tap-enroll',
                  uid,
                  classOrTest,
                  analyticsProps
                )
              );
              segment.track(GTM_TAP_ENROLL, analyticsProps);
            }
          });
        }
      }
    };

    const [pos, setPos] = useState(0);
    const [testCardHeight, setTestCardHeight] = useState(0);
    const [testCardWidth, setTestCardWidth] = useState(0);
    const testCardsContainer = useRef(null);
    useEffect(() => {
      setTestCardHeight(
        testCardsContainer.current ? testCardsContainer.current.offsetHeight : 0
      );
      setTestCardWidth(
        testCardsContainer.current ? testCardsContainer.current.offsetWidth : 0
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [enabledGoal, setEnabledGoal] = useState(goalData[0]?.key);
    const selectedGoal = useRef(goalData[0]);
    const onPressGoal = (goal, index) => {
      selectedGoal.current = goal;
      setEnabledGoal(goal.key);
      setPos(index);
    };

    const renderCards = (goal, index) => {
      const Wrapper = deviceHelper.isMobileDevice() ? Fragment : StyledCarousel;

      return (
        <TestsContainer
          index={index}
          key={goal}
          pos={pos}
          customHeight={testCardHeight}
          customWidth={testCardWidth}
        >
          <Wrapper
            showControls
            key="testseries"
            numberOfItems={2}
            gridColumn="span 24"
          >
            {items[goal]?.map((item) => {
              const test = item;
              test.isEnrolled = status[test.uid] || status[item.testSeriesUid];
              const nowDate = new Date();
              test.isQuizAvailable =
                new Date(test.startsAt) - nowDate <= 900000;
              test.isLive =
                new Date(test.startsAt) - nowDate <= 900000 &&
                new Date(test.endsAt) > nowDate;
              test.isGoingToBeLive =
                new Date(test.startsAt) - nowDate <= 900000 &&
                new Date(test.startsAt) - nowDate > 0;
              return (
                <Card
                  key={`testseries-${test.uid}`}
                  data={test}
                  onClickEnroll={onClickEnroll}
                  isEnrolled={test.isEnrolled}
                  gridColumn="span 8"
                />
              );
            })}
          </Wrapper>
        </TestsContainer>
      );
    };

    return (
      <BlocksWrapper ref={ref} id={id}>
        <LeftColumn>
          <img src={dateImage} alt="date icon" />
          <Heading>{title}</Heading>
          <GoalTabs>
            {goalData.map((goal, index) => {
              if (!items[goal.key]) return null;
              return (
                <GoalBlockContainer key={`goal-${goal.key}`}>
                  <GoalBlock
                    onClick={() => onPressGoal(goal, index)}
                    isEnabled={enabledGoal === goal.key}
                  >
                    <H4
                      color={
                        enabledGoal === goal.key
                          ? TextColors.BLACK
                          : TextColors.DISABLED
                      }
                    >
                      {goal.name}
                    </H4>
                  </GoalBlock>
                </GoalBlockContainer>
              );
            })}
          </GoalTabs>
        </LeftColumn>
        <RightColumn ref={testCardsContainer}>
          <TestCardsContainer
            customHeight={testCardHeight}
            customWidth={testCardWidth}
          >
            <InnerContainer
              customHeight={testCardHeight}
              pos={pos}
              customWidth={testCardWidth}
            >
              {typeof window !== 'undefined' &&
                goalData.map((goal, index) => renderCards(goal.key, index))}
            </InnerContainer>
          </TestCardsContainer>
        </RightColumn>
        <FreePlanUnlock
          drawerState={freePlanUnlockDrawerState}
          closeDrawerHandler={closeFreePlanUnlockDrawer}
          objectType={unlockFreePlanFor.COURSE}
          objectUid={objectUid}
        />
      </BlocksWrapper>
    );
  }
);

GoalWiseTestSeries.displayName = 'GoalWiseTestSeries';

GoalWiseTestSeries.propTypes = {
  items: PropTypes.objectOf(PropTypes.any),
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  goalData: PropTypes.arrayOf(PropTypes.object),
  Card: PropTypes.func.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired
};

GoalWiseTestSeries.defaultProps = {
  id: '',
  goalData: [],
  items: {}
};

export default GoalWiseTestSeries;
